import { PaginatedDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { get } from 'lodash';
import { CSSProperties, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListTable, ListTableProps } from '../list-table/list-table.component';
import { Path } from '../list-table/list-table.types';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';

export type InfiniteTableProps<T extends object, U extends Path<T>, Q extends object = T> = Omit<
  ListTableProps<T, U, Q>,
  'rows'
> & {
  pages: PaginatedRow<T>[];
  infiniteScroll: InfiniteScrollProps;
};

export interface InfiniteScrollProps {
  onScrollToBottom(): void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage(): void;
}

export type PaginatedRow<T extends object> = { items: T[] } & PaginatedDto;

/** Fetches the table may make on its own, with no user gesture, per rolling window. */
export const UNATTENDED_FETCH_BUDGET = 5;
export const UNATTENDED_FETCH_WINDOW_MS = 60_000;
/** A gesture older than this no longer authorises a fetch. */
const GESTURE_TTL_MS = UNATTENDED_FETCH_WINDOW_MS;
/** A scroll event counts as a gesture only this soon after a pointer press (scrollbar drags). */
const POINTER_SCROLL_GRACE_MS = 1000;
/** Grace period for virtualised rows to lay out before trusting the scroll-height check. */
const LAYOUT_SETTLE_MS = 300;
const GESTURE_KEYS = new Set(['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar']);

/** Flattens pages into rows, dropping duplicates by `uniqueKey` (a page-one refresh can push a row onto page two). */
export function dedupeRows<T extends object>(pages: Array<{ items: T[] }>, uniqueKey: string): T[] {
  const seen = new Set<unknown>();
  const rows: T[] = [];
  for (const page of pages) {
    for (const row of page.items ?? []) {
      const key = get(row, uniqueKey);
      if (key !== undefined) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      rows.push(row);
    }
  }
  return rows;
}

function useDocumentVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * Decides whether the table may fetch another page by itself: a user gesture always allows the next
 * fetch; without one, UNATTENDED_FETCH_BUDGET per UNATTENDED_FETCH_WINDOW_MS, so an idle page can
 * never silently walk the whole result set.
 */
function usePaginationGate() {
  const gestureAt = useRef(0);
  const unattendedFetchTimes = useRef<number[]>([]);
  const [exhausted, setExhausted] = useState(false);

  const markGesture = useCallback(() => {
    gestureAt.current = Date.now();
    setExhausted(false);
  }, []);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let lastPointerDown = 0;
    const onPointerDown = () => {
      lastPointerDown = Date.now();
    };
    // Scroll events also fire for programmatic scrolls (index restore, scroll-to-top), so only a
    // scroll shortly after a pointer press (scrollbar drag) counts as a gesture.
    const onScroll = () => {
      if (window.scrollY === lastScrollY) return;
      lastScrollY = window.scrollY;
      if (Date.now() - lastPointerDown < POINTER_SCROLL_GRACE_MS) markGesture();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (GESTURE_KEYS.has(e.key)) markGesture();
    };
    window.addEventListener('wheel', markGesture, { passive: true });
    window.addEventListener('touchmove', markGesture, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('wheel', markGesture);
      window.removeEventListener('touchmove', markGesture);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [markGesture]);

  // Re-open the gate once the oldest unattended fetch leaves the rolling window.
  useEffect(() => {
    if (!exhausted) return undefined;
    const oldest = unattendedFetchTimes.current[0];
    if (oldest === undefined) {
      setExhausted(false);
      return undefined;
    }
    const timer = window.setTimeout(
      () => setExhausted(false),
      Math.max(0, oldest + UNATTENDED_FETCH_WINDOW_MS - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [exhausted]);

  /** Returns true if a fetch may proceed now, consuming either the pending gesture or one unit of budget. */
  const tryConsume = useCallback(() => {
    const now = Date.now();
    if (gestureAt.current && now - gestureAt.current < GESTURE_TTL_MS) {
      gestureAt.current = 0;
      return true;
    }
    unattendedFetchTimes.current = unattendedFetchTimes.current.filter((t) => now - t < UNATTENDED_FETCH_WINDOW_MS);
    if (unattendedFetchTimes.current.length < UNATTENDED_FETCH_BUDGET) {
      unattendedFetchTimes.current.push(now);
      return true;
    }
    setExhausted(true);
    return false;
  }, []);

  return { tryConsume, markGesture, exhausted };
}

export function InfiniteTable<T extends object, U extends Path<T>, Q extends object = T>({
  infiniteScroll,
  pages,
  scrollUp = {
    scrollUpAtScrollY: 600
  },
  ...rest
}: InfiniteTableProps<T, U, Q>) {
  const uniqueKey = rest.uniqueKey as unknown as string;
  const rows = useMemo(() => dedupeRows(pages, uniqueKey), [pages, uniqueKey]);

  const tableRef = useRef<HTMLDivElement>(null);
  const gate = usePaginationGate();
  const visible = useDocumentVisible();

  // The props object is rebuilt every render; read it through a ref so the observer is not
  // recreated (and its initial callback re-fired) on unrelated renders.
  const infiniteScrollRef = useRef(infiniteScroll);
  infiniteScrollRef.current = infiniteScroll;

  const { tryConsume } = gate;
  const scrollToBottomObserver = useRef<IntersectionObserver>();
  const lastRowRef = useCallback(
    (node: HTMLElement | null) => {
      if (scrollToBottomObserver.current) {
        scrollToBottomObserver.current.disconnect();
        scrollToBottomObserver.current = undefined;
      }
      if (!node || infiniteScrollRef.current.isFetchingNextPage) {
        return;
      }
      scrollToBottomObserver.current = new IntersectionObserver((entries) => {
        // Ignore a loader that sits inside the initial viewport; the fill effect handles that case.
        const rect = node.getBoundingClientRect();
        const absoluteTop = rect.top + document.documentElement.scrollTop;
        if (!entries[0].isIntersecting || absoluteTop <= window.innerHeight) return;
        if (document.visibilityState === 'hidden') return;
        if (!tryConsume()) return;
        infiniteScrollRef.current.onScrollToBottom();
      });
      scrollToBottomObserver.current.observe(node);
    },
    [tryConsume]
  );

  useEffect(() => () => scrollToBottomObserver.current?.disconnect(), []);

  return (
    <>
      <div ref={tableRef}>
        <ListTable rows={rows} scrollUp={scrollUp} {...rest} />
      </div>
      <InfiniteScrollObserver
        hasRows={rows.length > 0}
        hasError={rest.hasError}
        infiniteScroll={infiniteScroll}
        lastRowRef={lastRowRef}
        gate={gate}
        visible={visible}
        tableRef={tableRef}
      />
    </>
  );
}

const loadMoreStyle: CSSProperties = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  width: '100%'
};

function InfiniteScrollObserver({
  hasRows,
  infiniteScroll,
  lastRowRef,
  hasError,
  gate,
  visible,
  tableRef
}: {
  infiniteScroll: InfiniteScrollProps;
  hasRows: boolean;
  lastRowRef(node: HTMLElement | null): void;
  hasError: boolean;
  gate: ReturnType<typeof usePaginationGate>;
  visible: boolean;
  tableRef: RefObject<HTMLDivElement>;
}) {
  const { tryConsume, markGesture, exhausted } = gate;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = infiniteScroll;

  // Fill tall viewports: fetch while the page has no scrollbar.
  useEffect(() => {
    if (!hasRows || hasError || !visible || exhausted || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }
    let timer: number | undefined;
    const attempt = (waitedForLayout: boolean) => {
      const isScrollable = document.documentElement.scrollHeight > window.innerHeight;
      if (isScrollable) return;
      // Virtualised rows lay out asynchronously; give them one chance before trusting the check.
      const rowsLaidOut = !!tableRef.current?.querySelector('tbody tr');
      if (!rowsLaidOut && !waitedForLayout) {
        timer = window.setTimeout(() => attempt(true), LAYOUT_SETTLE_MS);
        return;
      }
      if (tryConsume()) fetchNextPage();
    };
    attempt(false);
    return () => window.clearTimeout(timer);
  }, [hasRows, hasError, visible, exhausted, hasNextPage, isFetchingNextPage, fetchNextPage, tryConsume, tableRef]);

  if (!hasRows) {
    return null;
  }

  if (hasError) {
    return (
      <p onClick={fetchNextPage} style={loadMoreStyle}>
        Something went wrong. Tap to load more
      </p>
    );
  }

  if (exhausted && hasNextPage && !isFetchingNextPage) {
    const loadMore = () => {
      markGesture();
      if (tryConsume()) fetchNextPage();
    };
    return (
      <p
        role="button"
        tabIndex={0}
        data-testid="infinite-table-load-more"
        onClick={loadMore}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            loadMore();
          }
        }}
        style={loadMoreStyle}
      >
        Scroll or tap to load more
      </p>
    );
  }

  if (hasNextPage || isFetchingNextPage) {
    return (
      <div ref={isFetchingNextPage ? undefined : lastRowRef}>
        <LoadingIndicator size="small" />
      </div>
    );
  }

  return null;
}
